import { Divider } from '@mui/material';
import Box from '@mui/material/Box';
import MuiTab, { TabProps } from '@mui/material/Tab';
import MuiTabs from '@mui/material/Tabs';
import { styled, Theme } from '@mui/material/styles';
import React, {
  ComponentType,
  FC,
  Key,
  MouseEvent,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Dot from '../Dot';
import { Row } from '../Layout';
import { VerticalTabsProvider } from '../VerticalTabsContext';

type StyledDividerBoxProps = {
  spacingY?: keyof Theme['spacingSizes'];
};

const StyledDividerBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'spacingY',
})<StyledDividerBoxProps>(({ theme, spacingY = 'sm' }) => ({
  padding: theme.spacing(theme.spacingSizes[spacingY], 0),
  display: 'flex',
  alignItems: 'stretch',
}));

const Tabs = styled(MuiTabs, {
  shouldForwardProp: (prop) =>
    prop !== 'spacing' &&
    prop !== 'fullWidth' &&
    prop !== 'tabMargin' &&
    prop !== 'hideTabsDivider',
})<{
  spacing: keyof Theme['spacingSizes'] | 'none';
  fullWidth?: boolean;
  tabMargin?: keyof Theme['spacingSizes'];
  hideTabsDivider?: boolean;
}>(({ theme, spacing, fullWidth = true, tabMargin, hideTabsDivider }) => ({
  minHeight: 'unset',
  ...(!fullWidth && { width: 'fit-content' }),
  ...(hideTabsDivider && { borderBottom: 'none' }),
  '& .MuiTabs-list': {
    gap:
      spacing !== 'none' ? theme.spacing(theme.spacingSizes[spacing]) : 'unset',
    ...(tabMargin && {
      paddingLeft: theme.spacing(theme.spacingSizes[tabMargin]),
    }),
  },
}));

const TabPanel = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'hidden' && prop !== 'contentPadding' && prop !== 'fullHeight',
})<{
  hidden?: boolean;
  contentPadding?: keyof Theme['spacingSizes'];
  fullHeight?: boolean;
}>(({ theme, hidden, contentPadding, fullHeight }) => ({
  display: hidden ? 'none' : 'block',
  width: '100%',
  ...(contentPadding && {
    padding: theme.spacing(theme.spacingSizes[contentPadding]),
  }),
  ...(fullHeight && {
    flex: '1 1 auto',
  }),
}));

const Container = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'contentSpacing' && prop !== 'fullHeight',
})<{
  contentSpacing: keyof Theme['spacingSizes'] | 'none';
  fullHeight?: boolean;
}>(({ theme, contentSpacing, fullHeight }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap:
    contentSpacing === 'none'
      ? 0
      : theme.spacing(theme.spacingSizes[contentSpacing]),
  ...(fullHeight && {
    flex: '1 1 auto',
  }),
}));

const KeepAlivePanel: FC<{
  active: boolean;
  children: ReactNode;
  contentPadding?: keyof Theme['spacingSizes'];
  fullHeight?: boolean;
}> = ({ active, children, contentPadding, fullHeight }) => {
  const [mounted, setMounted] = useState(active);

  useEffect(() => {
    if (active) {
      setMounted(true);
    }
  }, [active]);

  if (!mounted) {
    return null;
  }

  return (
    <TabPanel
      hidden={!active}
      contentPadding={contentPadding}
      fullHeight={fullHeight}
    >
      {children}
    </TabPanel>
  );
};

type Props<T extends Key> = {
  tabs: Array<{
    label: string | ReactNode;
    key: T;
    content: ReactNode;
    urlParam?: string;
    dotColor?: string;
  }>;
  withDivider?: boolean;
  disableFullWidth?: boolean;
  useTabsUrls?: boolean;
  keepAlive?: boolean;
  spacing?: keyof Theme['spacingSizes'] | 'none';
  contentSpacing?: keyof Theme['spacingSizes'] | 'none';
  contentPadding?: keyof Theme['spacingSizes'];
  tabMargin?: keyof Theme['spacingSizes'];
  hideTabsDivider?: boolean;
  Tab?: ComponentType<TabProps>;
  defaultTab?: T;
  onTabChange?: (tabKey: T) => void;
  endAdornment?: ReactNode;
  fullHeight?: boolean;
};

const TabsPanel = <T extends Key>({
  tabs,
  useTabsUrls,
  disableFullWidth = false,
  endAdornment,
  keepAlive,
  spacing = 'lg',
  contentSpacing,
  contentPadding,
  Tab = MuiTab,
  defaultTab,
  withDivider,
  onTabChange,
  fullHeight = false,
  tabMargin,
  hideTabsDivider,
}: Props<T>): JSX.Element => {
  const { hash, pathname } = useLocation();
  const history = useHistory();

  const [localTab, setLocalTab] = useState<T | null>(() => {
    if (defaultTab) {
      return defaultTab;
    }

    return tabs[0]?.key ?? null;
  });

  const tabFromHash =
    useTabsUrls && hash
      ? tabs.find((tab) => tab.urlParam === hash.replace('#', ''))?.key ?? null
      : null;

  const currentTab = tabFromHash ?? localTab;

  type VerticalTabState = Partial<Record<T, string | null>>;
  const [verticalTabs, setVerticalTabs] = useState<VerticalTabState>({});

  const handleChange = (event: SyntheticEvent, newTabKey: T) => {
    if (
      (event as React.MouseEvent).metaKey ||
      (event as React.MouseEvent).ctrlKey
    )
      return;
    const newTab = tabs.find((tab) => tab.key === newTabKey);

    if (newTab?.urlParam && useTabsUrls) {
      history.push(`#${newTab.urlParam}`);
    } else {
      if (useTabsUrls && hash) {
        history.replace({ ...history.location, hash: '' });
      }
      setLocalTab(newTabKey);
    }

    onTabChange?.(newTabKey);
  };

  const currentTabContent = tabs.find((tab) => tab.key === currentTab)?.content;

  const tabsJsx = (
    <Tabs
      spacing={spacing}
      tabMargin={tabMargin}
      hideTabsDivider={hideTabsDivider}
      fullWidth={endAdornment ? false : !disableFullWidth}
      value={currentTab}
      onChange={handleChange}
      variant="scrollable"
      scrollButtons={false}
    >
      {tabs.flatMap((tab, index) => {
        const isLast = index === tabs.length - 1;

        return [
          <Tab
            key={tab.key}
            label={
              tab.dotColor ? (
                <Row centered spacing="xs">
                  {tab.label}
                  <Dot color={tab.dotColor} />
                </Row>
              ) : (
                tab.label
              )
            }
            value={tab.key}
            {...(useTabsUrls && tab.urlParam
              ? {
                  component: 'a',
                  href: `${pathname}#${tab.urlParam}`,
                  onClick: (e: MouseEvent) => {
                    if (e.metaKey || e.ctrlKey) return;
                    e.preventDefault();
                  },
                }
              : {})}
          />,
          !isLast && withDivider ? (
            <StyledDividerBox key={`${tab.key}-divider`}>
              <Divider key={`${tab.key}-divider`} orientation="vertical" />
            </StyledDividerBox>
          ) : null,
        ];
      })}
    </Tabs>
  );

  return (
    <Container contentSpacing={contentSpacing ?? 'lg'} fullHeight={fullHeight}>
      {endAdornment ? (
        <Row fullWidth centered justify="space-between">
          {tabsJsx}
          {endAdornment ?? null}
        </Row>
      ) : (
        tabsJsx
      )}

      <VerticalTabsProvider
        verticalTab={verticalTabs?.[currentTab] ?? null}
        setVerticalTab={(verticalTab) => {
          if (currentTab) {
            setVerticalTabs({ ...verticalTabs, [currentTab]: verticalTab });
          }
        }}
      >
        {keepAlive ? (
          tabs.map((tab) => (
            <KeepAlivePanel
              key={tab.key}
              active={currentTab === tab.key}
              contentPadding={contentPadding}
              fullHeight={fullHeight}
            >
              {tab.content}
            </KeepAlivePanel>
          ))
        ) : (
          <TabPanel contentPadding={contentPadding} fullHeight={fullHeight}>
            {currentTabContent}
          </TabPanel>
        )}
      </VerticalTabsProvider>
    </Container>
  );
};

export default TabsPanel;
